import type { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

/** Row height / list geometry are owned by the chip; the navigator only needs to reach rows by index and key. */
export interface SvzChipListNavigatorHooks<TRow> {
  rows: () => readonly TRow[];
  keyOf: (row: TRow) => string | null;
  isSelectable: (row: TRow) => boolean;
  selectedKeys: () => readonly string[];
  viewport: () => CdkVirtualScrollViewport | undefined;
  select: (row: TRow) => void;
}

const NAVIGATION_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' ']);

/**
 * Keyboard navigation over a virtualized option list. `z-select` navigates the `z-select-item`s it finds in the overlay
 * DOM, which inside a `cdk-virtual-scroll-viewport` are only the rendered rows; this navigator moves an active index
 * over the **full** list, scrolls the viewport to it and focuses the row once it is rendered. Escape is left to
 * `z-select` (it closes the dropdown).
 */
export class SvzChipListNavigator<TRow> {
  private activeIndex = -1;

  constructor(private readonly hooks: SvzChipListNavigatorHooks<TRow>) {}

  get active(): number {
    return this.activeIndex;
  }

  reset(): void {
    this.activeIndex = -1;
  }

  setActiveByKey(key: string): void {
    const index = this.hooks.rows().findIndex((row) => this.hooks.keyOf(row) === key);
    if (index >= 0) {
      this.activeIndex = index;
    }
  }

  /** Lands on the first selected row (or the first selectable one) when the dropdown opens. */
  focusInitial(): void {
    const rows = this.hooks.rows();
    const selected = new Set(this.hooks.selectedKeys());
    let index = rows.findIndex((row) => {
      const key = this.hooks.keyOf(row);
      return key !== null && selected.has(key);
    });
    if (index < 0) {
      index = rows.findIndex((row) => this.hooks.isSelectable(row));
    }
    if (index >= 0) {
      this.moveTo(index, this.firstRowOfBlock(index));
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    if (!NAVIGATION_KEYS.has(event.key)) {
      return;
    }
    event.preventDefault();
    // `z-select` listens to the same keys on the listbox; the wrapper owns navigation inside the viewport.
    event.stopPropagation();

    switch (event.key) {
      case 'ArrowDown':
        this.step(1);
        break;
      case 'ArrowUp':
        this.step(-1);
        break;
      case 'Home':
        this.moveTo(this.nextSelectable(-1, 1));
        break;
      case 'End':
        this.moveTo(this.nextSelectable(this.hooks.rows().length, -1));
        break;
      default: {
        const row = this.hooks.rows()[this.activeIndex];
        if (row !== undefined && this.hooks.isSelectable(row)) {
          this.hooks.select(row);
        }
      }
    }
  }

  private step(direction: 1 | -1): void {
    const rows = this.hooks.rows();
    if (!rows.length) {
      return;
    }
    let next = this.nextSelectable(this.activeIndex, direction);
    if (next < 0) {
      // wrap around, as `z-select` does
      next = this.nextSelectable(direction > 0 ? -1 : rows.length, direction);
    }
    this.moveTo(next);
  }

  private nextSelectable(from: number, direction: 1 | -1): number {
    const rows = this.hooks.rows();
    for (let index = from + direction; index >= 0 && index < rows.length; index += direction) {
      if (this.hooks.isSelectable(rows[index])) {
        return index;
      }
    }
    return -1;
  }

  private firstRowOfBlock(index: number): number {
    const rows = this.hooks.rows();
    let first = index;
    while (first > 0 && !this.hooks.isSelectable(rows[first - 1])) {
      first -= 1;
    }
    return first;
  }

  private moveTo(index: number, scrollIndex = index): void {
    if (index < 0) {
      return;
    }
    this.activeIndex = index;
    const viewport = this.hooks.viewport();
    if (!viewport) {
      return;
    }
    viewport.scrollToIndex(scrollIndex);
    const key = this.hooks.keyOf(this.hooks.rows()[index]);
    // The row may only exist after the viewport rendered the new range.
    setTimeout(() => this.focusRow(viewport, key));
  }

  private focusRow(viewport: CdkVirtualScrollViewport, key: string | null): void {
    if (key === null) {
      return;
    }
    const rows = Array.from(viewport.elementRef.nativeElement.querySelectorAll<HTMLElement>('z-select-item, [z-select-item]'));
    for (const row of rows) {
      if (row.getAttribute('value') === key) {
        row.focus();
        row.setAttribute('data-selected', '');
        row.setAttribute('aria-selected', 'true');
      } else {
        const selected = new Set(this.hooks.selectedKeys());
        const isSelected = selected.has(row.getAttribute('value') ?? '');
        if (!isSelected) {
          row.removeAttribute('data-selected');
          row.removeAttribute('aria-selected');
        }
      }
    }
  }
}
